import React, { useState, useEffect, useRef, useCallback } from 'react';
import { savedViewsAPI, SavedView } from '../api';

/**
 * SavedViews — reusable saved-filter control for any list module.
 *
 * Lists the current user's saved views for a module, lets them apply one,
 * save the current filter state as a new view, set a default, and delete.
 * The default view (if any) is auto-applied once on mount via onApply.
 *
 * The parent owns the actual filter state; this component only reads it
 * (currentFilters) when saving and pushes a selected view's filters back out
 * through onApply.
 */

interface SavedViewsProps {
  module: string;
  currentFilters: Record<string, any>;
  onApply: (filters: Record<string, any>) => void;
  /** Auto-apply the default saved view on first load. Default true. */
  autoApplyDefault?: boolean;
}

const SavedViews: React.FC<SavedViewsProps> = ({
  module,
  currentFilters,
  onApply,
  autoApplyDefault = true,
}) => {
  const [views, setViews] = useState<SavedView[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [activeViewId, setActiveViewId] = useState<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const defaultAppliedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await savedViewsAPI.list(module);
      const data = Array.isArray(res.data) ? res.data : [];
      setViews(data);
      // Apply the default view once, on first successful load.
      if (autoApplyDefault && !defaultAppliedRef.current) {
        defaultAppliedRef.current = true;
        const def = data.find((v) => v.is_default);
        if (def) {
          setActiveViewId(def.id);
          onApply(def.filters || {});
        }
      }
    } catch (err) {
      console.error('Failed to load saved views:', err);
    }
    // onApply intentionally omitted: we only want the default applied once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, autoApplyDefault]);

  useEffect(() => {
    load();
  }, [load]);

  // Close dropdown on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const applyView = (view: SavedView) => {
    setActiveViewId(view.id);
    onApply(view.filters || {});
    setOpen(false);
  };

  const handleSave = async () => {
    const name = newName.trim();
    if (!name) {
      setError('Enter a name for this view');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await savedViewsAPI.create({ module, name, filters: currentFilters });
      setViews((prev) => [...prev, res.data]);
      setActiveViewId(res.data.id);
      setNewName('');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save view');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (view: SavedView) => {
    try {
      await savedViewsAPI.remove(view.id);
      setViews((prev) => prev.filter((v) => v.id !== view.id));
      if (activeViewId === view.id) setActiveViewId(null);
    } catch (err) {
      console.error('Failed to delete saved view:', err);
    }
  };

  const handleSetDefault = async (view: SavedView) => {
    try {
      const makeDefault = !view.is_default;
      await savedViewsAPI.update(view.id, { is_default: makeDefault });
      setViews((prev) =>
        prev.map((v) => ({
          ...v,
          is_default: v.id === view.id ? makeDefault : makeDefault ? false : v.is_default,
        })),
      );
    } catch (err) {
      console.error('Failed to set default view:', err);
    }
  };

  const activeView = views.find((v) => v.id === activeViewId);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        title="Saved views"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
        <span>{activeView ? activeView.name : 'Saved Views'}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 100,
            width: '280px',
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          {/* Saved list */}
          <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
            {views.length === 0 ? (
              <div style={{ padding: '12px', fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>
                No saved views yet
              </div>
            ) : (
              views.map((view) => (
                <div
                  key={view.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    borderBottom: '1px solid #f1f5f9',
                    background: view.id === activeViewId ? '#f0fdfa' : 'white',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => applyView(view)}
                    style={{
                      flex: 1,
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: '#1e293b',
                      padding: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title="Apply this view"
                  >
                    {view.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSetDefault(view)}
                    title={view.is_default ? 'Default view (click to unset)' : 'Set as default'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', lineHeight: 1 }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill={view.is_default ? '#f59e0b' : 'none'} stroke={view.is_default ? '#f59e0b' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(view)}
                    title="Delete view"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', lineHeight: 1, color: '#94a3b8' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Save current */}
          <div style={{ padding: '10px 12px', borderTop: '1px solid #e2e8f0', background: '#f8f9fa' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
              Save current filters
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                type="text"
                value={newName}
                onChange={(e) => { setNewName(e.target.value); setError(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                placeholder="View name"
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '13px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  background: 'white',
                }}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
            {error && (
              <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '6px' }}>{error}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedViews;
